import { Injectable, inject, signal, computed } from '@angular/core';
import { NotificationApi, NotificationPayload, PushSubscription } from '../api/notification.api';

// SwPush type definition (optional dependency)
interface SwPushLike {
  isEnabled: boolean;
  subscription: {
    subscribe: (observer: { next: (sub: any) => void }) => void;
  };
  notificationClicks: {
    subscribe: (observer: { next: (click: any) => void }) => void;
  };
  requestSubscription(options: { serverPublicKey: string }): Promise<any>;
}

export enum PushPermissionStatus {
  pending = 'pending',
  granted = 'granted',
  denied = 'denied',
}

const VAPID_PUBLIC_KEY = '';
//'BBqgbENcfioGEWKbXHJp0nqKrAkkDgXzIa4Z-IWhzX0M90cnojTL0rhMrflpjw6QDGQ7cawCgeBhXl8UC-AUQzg';

@Injectable({ providedIn: 'root' })
export class NotificationStore {
  private notificationApi = inject(NotificationApi);

  // SwPush is optional - only available if @angular/service-worker is installed
  private swPush: SwPushLike | null = null;

  // ==================== Private Mutable Signals ====================
  private _subscribed = signal(false);
  private _permissionStatus = signal<PushPermissionStatus>(PushPermissionStatus.pending);
  private _loading = signal(false);
  private _error = signal<string | null>(null);
  private _isServiceWorkerEnabled = signal(false);
  private _vapidPublicKey = signal<string | null>(null);

  // ==================== Public Readonly Signals ====================
  readonly subscribed = this._subscribed.asReadonly();
  readonly permissionStatus = this._permissionStatus.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly isServiceWorkerEnabled = this._isServiceWorkerEnabled.asReadonly();
  readonly vapidPublicKey = this._vapidPublicKey.asReadonly();

  // ==================== Computed Signals ====================
  readonly canSubscribe = computed(() => this._isServiceWorkerEnabled() && !this._subscribed());
  readonly isGranted = computed(() => this._permissionStatus() === PushPermissionStatus.granted);
  readonly isDenied = computed(() => this._permissionStatus() === PushPermissionStatus.denied);
  readonly isPending = computed(() => this._permissionStatus() === PushPermissionStatus.pending);

  constructor() {
    // Try to inject SwPush if available (from @angular/service-worker)
    try {
      // Dynamic import - will only work if the package is installed
      this.initializeSwPush();
    } catch (error) {
      console.warn('SwPush not available. Push notifications will not work.', error);
      this._isServiceWorkerEnabled.set(false);
    }
  }

  private initializeSwPush(): void {
    // This will be injected if @angular/service-worker is available in the consumer app
    // For now, we'll check if it's available in the injector
    try {
      // Use dynamic injection to avoid compile-time dependency
      const injector = inject(inject as any);
      if (injector) {
        // SwPush would be injected here in apps that have @angular/service-worker
        // For lib build, we just set up the infrastructure
      }
    } catch {
      // SwPush not available
    }
  }

  // ==================== Operations ====================

  async getVapidPublicKey(): Promise<string> {
    // Return cached key if available
    const cachedKey = this._vapidPublicKey();
    if (cachedKey) {
      return cachedKey;
    }

    try {
      const key = await this.notificationApi.getVapidPublicKey();
      this._vapidPublicKey.set(key);
      return key;
    } catch {
      console.warn('Failed to fetch VAPID key from backend, using default');
      // Fallback to hardcoded key
      this._vapidPublicKey.set(VAPID_PUBLIC_KEY);
      return VAPID_PUBLIC_KEY;
    }
  }

  /**
   * Subscribe to push notifications
   * @returns Promise with permission status
   */
  async subscribeToNotifications(): Promise<PushPermissionStatus> {
    if (!this.swPush || !this.swPush.isEnabled) {
      const errorMsg = 'Service worker is not enabled or not available';
      this._error.set(errorMsg);
      throw new Error(errorMsg);
    }

    if (this._subscribed()) {
      return PushPermissionStatus.granted;
    }

    this._loading.set(true);
    this._error.set(null);

    try {
      const browserPermission = await this.requestBrowserPermission();
      this._permissionStatus.set(browserPermission);

      if (browserPermission !== PushPermissionStatus.granted) {
        return browserPermission;
      }

      // Fetch VAPID key from backend
      const vapidKey = await this.getVapidPublicKey();

      const sub = await this.swPush.requestSubscription({
        serverPublicKey: vapidKey,
      });

      const host = this.getApplicationHost();
      const pushSubscription: PushSubscription = sub.toJSON() as PushSubscription;

      await this.notificationApi.subscribeToPush(pushSubscription, host);

      this._subscribed.set(true);
      this._permissionStatus.set(PushPermissionStatus.granted);

      return PushPermissionStatus.granted;
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to subscribe to notifications';
      this._error.set(errorMsg);

      // Keep permission aligned with browser state instead of always forcing denied.
      const permissionStatus = this.getPermissionStatusFromBrowser();
      this._permissionStatus.set(permissionStatus);
      console.error('Could not subscribe to notifications', error);

      return permissionStatus;
    } finally {
      this._loading.set(false);
    }
  }

  private async requestBrowserPermission(): Promise<PushPermissionStatus> {
    if (typeof Notification === 'undefined') {
      return PushPermissionStatus.pending;
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return this.mapBrowserPermission(permission);
    }

    return this.mapBrowserPermission(Notification.permission);
  }

  private getPermissionStatusFromBrowser(): PushPermissionStatus {
    if (typeof Notification === 'undefined') {
      return PushPermissionStatus.pending;
    }

    return this.mapBrowserPermission(Notification.permission);
  }

  private mapBrowserPermission(permission: NotificationPermission): PushPermissionStatus {
    switch (permission) {
      case 'granted':
        return PushPermissionStatus.granted;
      case 'denied':
        return PushPermissionStatus.denied;
      default:
        return PushPermissionStatus.pending;
    }
  }

  /**
   * Set SwPush instance (called by consumer apps that have @angular/service-worker)
   * @param swPush - SwPush instance
   */
  setSwPush(swPush: SwPushLike): void {
    this.swPush = swPush;
    this._isServiceWorkerEnabled.set(swPush.isEnabled);

    if (swPush.isEnabled) {
      swPush.subscription.subscribe({
        next: (sub: any) => {
          const isSubscribed = sub !== null;
          this._subscribed.set(isSubscribed);
          this._permissionStatus.set(
            isSubscribed ? PushPermissionStatus.granted : PushPermissionStatus.pending
          );
        },
      });

      swPush.notificationClicks.subscribe({
        next: (click: any) => {
          this.handleNotificationClick(click);
        },
      });
    }
  }

  /**
   * Acknowledge received notification
   * @param notificationID - Notification ID
   * @returns Promise with acknowledgement result
   */
  async acknowledgeNotification(notificationID: number): Promise<void> {
    try {
      await this.notificationApi.acknowledgeNotification(notificationID);
    } catch (error) {
      console.error('Failed to acknowledge notification:', error);
      throw error;
    }
  }

  /**
   * Send test notification
   * @returns Promise with test result
   */
  async sendTestNotification(): Promise<void> {
    const payload: NotificationPayload = {
      notification: {
        title: 'Angular News',
        body: 'Newsletter Available!',
        icon: 'assets/main-page-logo-small-hat.png',
        vibrate: [100, 50, 100],
        data: {
          dateOfArrival: Date.now(),
          primaryKey: 1,
        },
        actions: [
          {
            action: 'explore',
            title: 'Go to the site',
          },
        ],
      },
    };

    try {
      await this.notificationApi.sendTestNotification(payload);
    } catch (error) {
      console.error('Failed to send test notification:', error);
      throw error;
    }
  }

  // ==================== Private Methods ====================

  private handleNotificationClick(click: any): void {
    const notificationID = click.notification?.data?.notificationID;

    if (!notificationID) {
      console.warn('Notification click without ID');
      return;
    }

    // Acknowledge notification for all actions
    this.acknowledgeNotification(notificationID).catch((error) => {
      console.error('Failed to acknowledge notification on click:', error);
    });

    // Handle specific actions
    switch (click.action) {
      case 'explore':
        // Navigation could be handled by emitting an event or using Router
        break;
      case 'dismiss':
        // Notification already acknowledged
        break;
      default:
        // Default action
        break;
    }
  }

  private getApplicationHost(): string {
    const temp = window.location.href.split('/');
    const baseTags = document.getElementsByTagName('base');
    const basePath = baseTags.length ? baseTags[0].href.substr(location.origin.length, 999) : '';

    let host = temp[0] + '//' + temp[2] + basePath;

    if (host.lastIndexOf('/') === host.length - 1) {
      host = host.slice(0, host.length - 1);
    }

    return host;
  }

  // ==================== State Management ====================

  /**
   * Clear error state
   */
  clearError(): void {
    this._error.set(null);
  }

  /**
   * Clear all state
   */
  clear(): void {
    this._error.set(null);
    this._loading.set(false);
    // Note: subscribed and permission status are maintained by SwPush
  }
}
